import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  SubmissionEditLockService,
  isLockExpiredError
} from '../core/submission-edit-lock/submission-edit-lock.service';
import { EDIT_LOCK_EXPIRED_MESSAGE, EDIT_LOCK_TOKEN_HEADER } from '../core/submission-edit-lock/submission-edit-lock.constants';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Submission save/write endpoints that must carry the edit-lock token. */
export const EDIT_LOCK_PROTECTED_PATHS = [
  'basic-details',
  'applicant-details',
  'property-details',
  'underwriting-information',
  'coverage-preferences',
  'additional-interests',
  'submission/status',
  'trigger-risk-api',
  'generate-quote'
];

export function isEditLockProtectedRequest(method: string, url: string): boolean {
  if (!WRITE_METHODS.has(method.toUpperCase()) || !environment.api_url || !url.startsWith(environment.api_url)) {
    return false;
  }
  const path = url.slice(environment.api_url.length).split('?')[0].replace(/\/+$/, '');
  return EDIT_LOCK_PROTECTED_PATHS.includes(path);
}

export const editLockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isEditLockProtectedRequest(req.method, req.url)) {
    return next(req);
  }

  const lockService = inject(SubmissionEditLockService);
  // INPROGRESS-HITL / UW_REVIEW / UW_REQUOTE submissions are not lock-protected: send the request untouched.
  if (lockService.isExempt()) {
    return next(req);
  }

  // A quiet re-acquire is in flight: wait for it, then send with the new token.
  const reacquire$ = lockService.pendingReacquire();
  if (reacquire$) {
    return reacquire$.pipe(
      switchMap(ok => ok ? sendWithLock(req, next, lockService) : lockExpiredError(req.url))
    );
  }
  return sendWithLock(req, next, lockService);
};

function sendWithLock(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  lockService: SubmissionEditLockService
): Observable<HttpEvent<unknown>> {
  if (lockService.isLost()) {
    return lockExpiredError(req.url);
  }

  const token = lockService.getToken();
  return next(withToken(req, token)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!token || !isLockExpiredError(error)) {
        return throwError(() => error);
      }
      // The token was released elsewhere (e.g. a duplicate tab closed). A rejected save wrote nothing, so
      // re-acquire (or reuse a token a heartbeat already re-acquired) and retry once.
      const current = lockService.getToken();
      const ready$ = current && current !== token ? of(true) : lockService.reacquire();
      return ready$.pipe(
        switchMap(ok => {
          const newToken = lockService.getToken();
          if (!ok || !newToken) {
            // Re-acquire failed: the service already showed the dialog.
            return throwError(() => error);
          }
          return next(withToken(req, newToken)).pipe(
            catchError((retryError: HttpErrorResponse) => {
              if (isLockExpiredError(retryError)) {
                lockService.notifyLockExpired(retryError?.error?.message);
              }
              return throwError(() => retryError);
            })
          );
        })
      );
    })
  );
}

function withToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token ? req.clone({ setHeaders: { [EDIT_LOCK_TOKEN_HEADER]: token } }) : req;
}

function lockExpiredError(url: string): Observable<never> {
  return throwError(() => new HttpErrorResponse({
    status: 409,
    statusText: 'Conflict',
    url,
    error: { success: false, message: EDIT_LOCK_EXPIRED_MESSAGE }
  }));
}
